import { useEffect, useRef } from "react";

import { posthog as posthogClient } from "posthog-js";
import { useFeatureFlagVariantKey } from "posthog-js/react";

import { env } from "@musicfy/env.mjs";
import { useAnalytics } from "@musicfy/libs/AnalyticsProvider";

/**
 * Pricing experiments wired through PostHog feature flags.
 *
 * To launch a new variant:
 *  1. Create the Paddle coupon (for discount tests) in the Paddle dashboard.
 *  2. Add the variant key + config to the corresponding map below.
 *  3. Add the same variant key in the PostHog flag.
 */

// ----- #2 Discount swap -----------------------------------------------------

export const DISCOUNT_FLAG_KEY = "pricing-discount-test";

type DiscountVariantConfig = {
  paddleDiscountId: string;
  /** Shown in the strikethrough chip on the PlanCard. */
  label: string;
  /** Shown in the WelcomeOffer banner subtitle when no banner-copy override. */
  periodSuffixMonthly: string;
  periodSuffixYearly: string;
};

const DISCOUNT_VARIANTS: Record<string, DiscountVariantConfig> = {
  control: {
    paddleDiscountId:
      env.NEXT_PUBLIC_VERCEL_ENV === "production"
        ? "dsc_01hs1ghkgstga49dqm0r3wpys2"
        : "dsc_01hhkrg40tqeb2cdzkwzkthp67",
    label: "60% off",
    periodSuffixMonthly: "60% off your first month",
    periodSuffixYearly: "60% off your first year",
  },
  // Add real coupon IDs in Paddle, then mirror them here:
  // "variant-a": {
  //   paddleDiscountId: "dsc_…",
  //   label: "75% off",
  //   periodSuffixMonthly: "75% off your first month",
  //   periodSuffixYearly: "75% off your first year",
  // },
};

// ----- #3 Yearly-only -------------------------------------------------------

export const INTERVALS_FLAG_KEY = "pricing-intervals-test";

type IntervalsVariantConfig = {
  showMonthly: boolean;
  showYearly: boolean;
  default: "month" | "year";
};

const INTERVALS_VARIANTS: Record<string, IntervalsVariantConfig> = {
  control: { showMonthly: true, showYearly: true, default: "year" },
  "yearly-only": { showMonthly: false, showYearly: true, default: "year" },
};

// ----- #4 Banner copy -------------------------------------------------------

export const BANNER_COPY_FLAG_KEY = "pricing-banner-copy-test";

type BannerCopyVariantConfig = {
  /** When null, fall back to the active discount variant's period suffix. */
  title: string | null;
  subtitle: string | null;
  eyebrow: string;
};

const BANNER_COPY_VARIANTS: Record<string, BannerCopyVariantConfig> = {
  control: { title: null, subtitle: null, eyebrow: "Welcome offer" },
  // "variant-a": {
  //   title: "Save big — today only",
  //   subtitle: "Your one-time welcome bonus",
  //   eyebrow: "Limited offer",
  // },
};

// ----- Hook -----------------------------------------------------------------

const resolveVariant = <T>(
  rawKey: string | boolean | undefined,
  variants: Record<string, T>
): { key: string; config: T } => {
  const key =
    typeof rawKey === "string" && rawKey in variants ? rawKey : "control";
  return { key, config: variants[key] ?? variants.control! };
};

/**
 * Which resolved pricing-experiment variants to persist on the Paddle subscription (via checkout
 * customData) so the day-35 RENEWAL — which has no client session, and thus no PostHog $feature
 * super-property — can still be attributed to the arm the buyer was in. Takes the RAW
 * useFeatureFlagVariantKey values: includes a flag only when PostHog resolved it to a real string
 * (so a resolved "control" IS included — the buyer really is in control), and skips `undefined`
 * (unresolved — never assigned) / `null` / booleans. This never sees resolveVariant's
 * undefined→"control" fallback, so it can't attribute a renewal to a phantom the buyer never saw.
 * Pure — unit-tested.
 */
export const pricingVariantsForCheckout = (raw: {
  discount?: string | boolean | null;
  intervals?: string | boolean | null;
  bannerCopy?: string | boolean | null;
}): Record<string, string> => {
  const out: Record<string, string> = {};
  if (typeof raw.discount === "string") out[DISCOUNT_FLAG_KEY] = raw.discount;
  if (typeof raw.intervals === "string")
    out[INTERVALS_FLAG_KEY] = raw.intervals;
  if (typeof raw.bannerCopy === "string")
    out[BANNER_COPY_FLAG_KEY] = raw.bannerCopy;
  return out;
};

export const usePricingExperiments = () => {
  const { trackEvent } = useAnalytics();

  const discountRaw = useFeatureFlagVariantKey(DISCOUNT_FLAG_KEY);
  const intervalsRaw = useFeatureFlagVariantKey(INTERVALS_FLAG_KEY);
  const bannerCopyRaw = useFeatureFlagVariantKey(BANNER_COPY_FLAG_KEY);

  const discount = resolveVariant(discountRaw, DISCOUNT_VARIANTS);
  const intervals = resolveVariant(intervalsRaw, INTERVALS_VARIANTS);
  const bannerCopy = resolveVariant(bannerCopyRaw, BANNER_COPY_VARIANTS);

  // True once PostHog has actually resolved every flag. Until then the keys
  // above are placeholder "control" values from the fallback in resolveVariant,
  // which would otherwise pollute exposure counts with phantom control hits.
  const ready =
    discountRaw !== undefined &&
    intervalsRaw !== undefined &&
    bannerCopyRaw !== undefined;

  const exposedRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    if (!ready) return;

    // Register variants as PostHog super properties so every downstream event
    // (Pricing Page Viewed, Checkout Started, Subscription Upgrade, ...) is
    // tagged with the user's variant without each call site needing to know.
    posthogClient.register({
      [`$feature/${DISCOUNT_FLAG_KEY}`]: discount.key,
      [`$feature/${INTERVALS_FLAG_KEY}`]: intervals.key,
      [`$feature/${BANNER_COPY_FLAG_KEY}`]: bannerCopy.key,
    });

    const fire = (flag: string, variant: string) => {
      const id = `${flag}:${variant}`;
      if (exposedRef.current.has(id)) return;
      exposedRef.current.add(id);
      trackEvent("Pricing Experiment Exposed", { flag, variant });
    };
    fire(DISCOUNT_FLAG_KEY, discount.key);
    fire(INTERVALS_FLAG_KEY, intervals.key);
    fire(BANNER_COPY_FLAG_KEY, bannerCopy.key);
  }, [ready, discount.key, intervals.key, bannerCopy.key, trackEvent]);

  return { discount, intervals, bannerCopy, ready };
};
